import { createContext, useState, useEffect } from 'react';
import { getUserDataFromToken, getActrlDoc } from '../../api/auth';
import { ACCESS_CONTROL_CONSTANTS } from '../../core/constants';
export const UserContext = createContext();

const { UI_ACCESS, TYPES } = ACCESS_CONTROL_CONSTANTS;

const UserProvider = ({ children }) => {
	const [user, setUser] = useState(getUserDataFromToken());
	const [accessControlDoc, setAccessControlDoc] = useState(null);

	/**
	 * Fetch the acessControlDoc
	 */
	useEffect(() => {
		const controller = new AbortController();
		(async () => {
			if (user) {
				try {
					const doc = await getActrlDoc(controller.signal);
					setAccessControlDoc(doc?.data?.data?.actrlDoc || null);
				} catch (err) {
					console.log(err);
				}
			}
		})();
		return () => {
			controller.abort();
		};
	}, [user]);

	/**
	 * @name getAccessControlSet
	 * @description This function returns a set of strings that signifies if this input/sidebar setting/accordion etc is either visible/disabled/hidden. Takes in system, type and identifier as arguments.
	 * @param {string} system This refers to the which system the access control is asked for, ie. 'FRAMERATE' , 'ADRECOVER' , 'GENERAL' etc.
	 * @param {string} type This refers to the type for which access control asked for, ie. 'INPUT_NAMES' , 'BUTTON_NAMES' , 'TAB_NAMES' , 'SETTING_NAMES' etc.
	 * @param {string} identifier This refers to the string identifier of the particular type through which its presence would be checked in the actrl doc.
	 * @returns {Set<string>}
	 */
	const getAccessControlSet = (system, type, identifier) => {
		const accessControlSet = new Set();
		const allDataPresentToGetAccessControlSet =
			user &&
			user.controlledAccess &&
			user.controlledAccess.length &&
			accessControlDoc &&
			system &&
			type &&
			identifier;

		if (allDataPresentToGetAccessControlSet) {
			for (let accessName of user.controlledAccess) {
				const hiddenIdentifiers = accessControlDoc[accessName]?.[system]?.HIDE?.[type] || [];
				const isIdentifierHidden = hiddenIdentifiers?.find((idn) => {
					return new RegExp(idn).test(identifier);
				});
				if (isIdentifierHidden) {
					accessControlSet.add(UI_ACCESS.HIDE);
				}

				// Only inputs and buttons support disable access control
				if (type === TYPES.INPUT_NAMES || type === TYPES.BUTTON_NAMES) {
					const disabledIdentifiers = accessControlDoc[accessName]?.[system]?.DISABLE?.[type] || [];
					const isIdentifierDisabled = disabledIdentifiers?.find((idn) => {
						return new RegExp(idn).test(identifier);
					});
					if (isIdentifierDisabled) {
						accessControlSet.add(UI_ACCESS.DISABLE);
					}
				}
			}
		}
		if (accessControlSet.size === 0) {
			accessControlSet.add(UI_ACCESS.VISIBLE);
		}
		return accessControlSet;
	};

	return (
		<UserContext.Provider value={{ user, setUser, getAccessControlSet }}>
			{children}
		</UserContext.Provider>
	);
};
export default UserProvider;
